import React, { useState, useMemo, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUser, faLock, faSpinner, faUserTie } from "@fortawesome/free-solid-svg-icons";
import { useTheme } from '../contexts/ThemeContext';
import authService from '../services/authService';
import TermsOfUse from './TermsOfUse';
import './Signup.css';

// Predefined list of titles
const USER_TITLES = [
  'Mr.',
  'Mrs.',
  'Ms.',
  'Miss',
  'Mx.',
  'Dr.',
  'Professor',
  'Associate Professor',
  'Assistant Professor',
  'Adjunct Professor',
  'Lecturer',
  'Instructor',
  'Faculty',
  'Student Doctor',
  'Medical Student',
  'Pre-Med Student',
  'Resident Physician',
  'Resident',
  'Fellow',
  'Intern',
  'Physician',
  'Physician Assistant',
  'Nurse Practitioner',
  'Registered Nurse',
  'Clinical Pharmacist',
  'Paramedic',
  'EMT'
];

// Helper function to get user-friendly error messages for different registration result types
const getErrorMessageForResult = (result) => {
  const resultLower = result?.toLowerCase() || '';
  switch (resultLower) {
    case 'invalidtoken':
      return 'The activation link is invalid. Please request a new activation email.';
    case 'expiredtoken':
      return 'The activation link has expired. Please request a new activation email.';
    case 'alreadyactivated':
      return 'This account has already been activated. Please log in.';
    case 'usernotfound':
      return 'The user account was not found. Please register again.';
    default:
      return 'Registration failed. Please try again or request a new activation email.';
  }
};

function ConfirmRegistration() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();
  
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    password: "",
    confirmPassword: "",
    title: ""
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [showTerms, setShowTerms] = useState(false);

  useEffect(() => {
    // Extract token from URL if not in params (in case it's in query string)
    const urlParams = new URLSearchParams(window.location.search);
    const urlToken = urlParams.get('token') || token;
    
    if (!urlToken) {
      setError("Invalid or missing activation token. Please check your email link.");
    }
  }, [token]);

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const validateForm = () => {
    if (!formData.firstName.trim()) {
      return "First name is required";
    }
    if (!formData.lastName.trim()) {
      return "Last name is required";
    }
    if (formData.password.length < 8) {
      return "Password must be at least 8 characters";
    }
    if (formData.password !== formData.confirmPassword) {
      return "Passwords do not match";
    }
    if (!formData.title) {
      return "Please select a title";
    }
    if (!acceptedTerms) {
      return "You must accept the Terms of Use to complete registration";
    }
    return null;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError("");
    setIsLoading(true);

    try {
      const validationError = validateForm();
      if (validationError) {
        throw new Error(validationError);
      }

      const urlParams = new URLSearchParams(window.location.search);
      const activationToken = urlParams.get('token') || token;
      
      if (!activationToken) {
        throw new Error("Invalid or missing activation token");
      }

      const response = await authService.confirmRegistration(activationToken, {
        firstName: formData.firstName,
        lastName: formData.lastName,
        password: formData.password,
        title: formData.title
      });

      // Check the result field in the response to determine if registration was successful
      if (response.result && response.result.toLowerCase() !== 'success') {
        // Handle different failure cases
        const errorMessage = response.message || getErrorMessageForResult(response.result);
        throw new Error(errorMessage);
      }

      setSuccess(true);
      setTimeout(() => {
        navigate('/login', { 
          state: { 
            message: 'Registration completed successfully! Please log in with your credentials.' 
          }
        });
      }, 2000);

    } catch (error) {
      console.error('Registration confirmation error:', error);
      
      // Check if the error contains result data from the API (400 response with result field)
      let errorMessage;
      if (error.data && error.data.result) {
        // API returned an error response with result data
        errorMessage = error.data.message || getErrorMessageForResult(error.data.result);
      } else if (error.message) {
        errorMessage = error.message;
      } else {
        errorMessage = "Registration failed. Please try again or request a new activation email.";
      }
      
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  // Theme-specific styles
  const themeStyles = useMemo(() => {
    return {
      container: {
        background: isDarkMode 
          ? 'linear-gradient(180deg, #0e1626 0%, #0b1220 100%)'
          : '#eceff4'
      },
      content: {
        background: isDarkMode 
          ? 'linear-gradient(145deg, #151e30 0%, #121a2c 100%)'
          : 'linear-gradient(145deg, #ffffff 0%, #f5f9ff 100%)',
        boxShadow: isDarkMode 
          ? '0 6px 12px rgba(0, 0, 0, 0.3)' 
          : '0 6px 12px rgba(0, 0, 0, 0.1)'
      },
      text: {
        primary: isDarkMode ? '#ffffff' : '#2c3e50',
        secondary: isDarkMode ? '#e1e9ff' : 'rgba(44, 62, 80, 0.8)'
      },
      button: {
        background: isDarkMode ? '#0074cc' : '#1976d2',
        color: '#ffffff'
      },
      link: {
        color: isDarkMode ? '#90cdf4' : '#3498db'
      },
      select: {
        background: isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'transparent',
        color: isDarkMode ? '#ffffff' : '#2c3e50',
        border: isDarkMode ? '1px solid rgba(255, 255, 255, 0.3)' : '1px solid rgba(44, 62, 80, 0.3)'
      }
    };
  }, [isDarkMode]);

  if (success) {
    return (
      <div className={`login-page-container ${isDarkMode ? 'dark-mode' : 'light-mode'}`} style={themeStyles.container}>
        <div className="login-container-main" style={themeStyles.content}>
          <div className="success-message" style={{ textAlign: 'center', padding: '40px' }}>
            <h2 style={{ color: themeStyles.text.primary, marginBottom: '20px' }}>
              Registration Complete!
            </h2>
            <p style={{ color: themeStyles.text.secondary }}>
              Your account has been successfully created. Redirecting to login...
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`login-page-container ${isDarkMode ? 'dark-mode' : 'light-mode'}`} style={themeStyles.container}>
      <div className="login-container-main" style={themeStyles.content}>
        <div className="login-header">
          <img 
            src={isDarkMode ? '/dark_green.png' : '/white_black.png'} 
            alt="ExamFlip Logo" 
            className="header-logo"
          />
          <h2 style={{ color: themeStyles.text.primary, marginTop: '10px' }}>
            Complete Your Registration
          </h2>
        </div>
        
        <form onSubmit={handleSubmit} className="login-form">
          <div className="input-group">
            <FontAwesomeIcon 
              icon={faUser} 
              className="auth-input-icon"
              style={{ color: themeStyles.text.primary }}
            />
            <input
              type="text"
              name="firstName"
              placeholder="First Name"
              value={formData.firstName}
              onChange={handleInputChange}
              required
              className="auth-input"
              disabled={isLoading}
            />
          </div>

          <div className="input-group">
            <FontAwesomeIcon 
              icon={faUser} 
              className="auth-input-icon"
              style={{ color: themeStyles.text.primary }}
            />
            <input
              type="text"
              name="lastName"
              placeholder="Last Name"
              value={formData.lastName}
              onChange={handleInputChange}
              required
              className="auth-input"
              disabled={isLoading}
            />
          </div>

          <div className="input-group">
            <FontAwesomeIcon 
              icon={faUserTie} 
              className="auth-input-icon"
              style={{ color: themeStyles.text.primary }}
            />
            <select
              name="title"
              value={formData.title}
              onChange={handleInputChange}
              required
              className="auth-input"
              disabled={isLoading}
              style={themeStyles.select}
            >
              <option value="">Select Title</option>
              {USER_TITLES.map((title) => (
                <option key={title} value={title}>
                  {title}
                </option>
              ))}
            </select>
          </div>

          <div className="input-group">
            <FontAwesomeIcon 
              icon={faLock} 
              className="auth-input-icon"
              style={{ color: themeStyles.text.primary }}
            />
            <input
              type="password"
              name="password"
              placeholder="Password"
              value={formData.password}
              onChange={handleInputChange}
              required
              className="auth-input"
              disabled={isLoading}
            />
          </div>

          <div className="input-group">
            <FontAwesomeIcon 
              icon={faLock} 
              className="auth-input-icon"
              style={{ color: themeStyles.text.primary }}
            />
            <input
              type="password"
              name="confirmPassword"
              placeholder="Confirm Password"
              value={formData.confirmPassword}
              onChange={handleInputChange}
              required
              className="auth-input"
              disabled={isLoading}
            />
          </div>
          
          {/* Terms of Use Checkbox */}
          <div className="terms-checkbox-container" style={{ 
            marginBottom: '20px', 
            display: 'flex', 
            alignItems: 'flex-start', 
            gap: '10px',
            fontSize: '14px',
            lineHeight: '1.4'
          }}>
            <input
              type="checkbox"
              id="terms-checkbox-confirm"
              checked={acceptedTerms}
              onChange={(e) => setAcceptedTerms(e.target.checked)}
              style={{
                marginTop: '2px',
                cursor: 'pointer',
                minWidth: '16px',
                height: '16px'
              }}
              disabled={isLoading}
            />
            <label 
              htmlFor="terms-checkbox-confirm" 
              style={{ 
                color: themeStyles.text.secondary,
                cursor: 'pointer',
                userSelect: 'none'
              }}
            >
              I have read and agree to the{' '}
              <button
                type="button"
                onClick={() => setShowTerms(true)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: themeStyles.link.color,
                  textDecoration: 'underline',
                  cursor: 'pointer',
                  padding: 0,
                  font: 'inherit'
                }}
                disabled={isLoading}
              >
                Terms of Use
              </button>
            </label>
          </div>
          
          {error && (
            <div className="error-message" style={{ color: '#e53e3e', marginBottom: '15px', textAlign: 'center' }}>
              {error}
            </div>
          )}
          
          <button 
            type="submit" 
            className="login-button"
            disabled={isLoading || !acceptedTerms}
            style={{
              background: (isLoading || !acceptedTerms) ? '#ccc' : themeStyles.button.background,
              color: themeStyles.button.color,
              cursor: (isLoading || !acceptedTerms) ? 'not-allowed' : 'pointer'
            }}
          >
            {isLoading ? (
              <>
                <FontAwesomeIcon icon={faSpinner} spin style={{ marginRight: '8px' }} />
                Completing registration...
              </>
            ) : (
              'Complete Registration'
            )}
          </button>
        </form>

        <div className="register-now">
          <p style={{ color: themeStyles.text.primary }}>
            Already have an account? <Link to="/login" style={{ color: themeStyles.link.color }}>Sign in</Link>
          </p>
        </div>
      </div>
      
      {/* Terms of Use Modal */}
      <TermsOfUse 
        isOpen={showTerms} 
        onClose={() => setShowTerms(false)} 
      />
    </div>
  );
}

export default ConfirmRegistration;
